import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, debounceTime, switchMap, of, tap, catchError } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { CompanyService } from '../services/company.service';
import { SignOrderService } from '../services/sign-order.service';
import { QrPosterDocumentService } from '../services/qr-poster-document.service';
import {
  QrPosterPdfPreviewRender,
  QrPosterPdfPreviewService,
} from '../services/qr-poster-pdf-preview.service';
import { DOMAIN } from '../constants/app.constants';
import { Company } from '../company/company.models';
import {
  CatalogProduct,
  BillingAddress,
  CatalogResponse,
  CreateSignOrderDraftRequest,
  CreateOrderRequest,
  OrderDetailResponse,
  PosterPreviewRequest,
  PosterPreviewResponse,
  PosterPreviewProperty,
  PricingTier,
  PropertyItem,
  QuoteRequest,
  QuoteResponse,
  SelectAllResponse,
  SignOrderDraft,
  SignOrderDraftResumeResponse,
  SignOrderDraftBillingAddress,
  SignOrderDraftShippingAddress,
  ShippingAddress,
  UpdateSignOrderDraftRequest,
} from '../models/sign-order.models';
import { splitAddress } from '../property-list/property-list.helpers';
import { formatSignOrderDateTime } from './sign-order-date.utils';
import { formatSignOrderWeight } from './sign-order-weight.utils';
import { QrPosterTemplate } from '../models/qr-poster-template.model';

interface SelectAllData {
  selectionId: string;
  total: number;
  search: string;
  excludedIds: Set<string>;
  defaultCopies: number;
  copyOverrides: Record<string, number>;
}

interface SelectionState {
  selectedPropertyIds: Set<string>;
  copiesMap: Record<string, number>;
  selectAllData: SelectAllData | null;
}

interface SelectedPropertyItem extends PropertyItem {
  copies: number;
}

interface CopyOverridePropertyItem extends PropertyItem {
  copies: number;
  isExcluded: boolean;
  hasCopyOverride: boolean;
}

interface PriceSummary {
  lowestUnitNetCents: number;
  highestUnitNetCents: number;
  isRange: boolean;
}

type ManualPropertyMetadata = Pick<PropertyItem, 'id' | 'address' | 'reference'>;

type CheckoutStep = 0 | 1 | 2 | 3 | 4 | 5;
type VisibleCheckoutStep = Exclude<CheckoutStep, 5>;

interface DisplayPricingTier {
  readonly minQuantity: number;
  readonly maxQuantity: number | null;
  readonly unitNetCents: number;
}

type PosterPageDimensions = Pick<QrPosterTemplate['page'], 'widthMm' | 'heightMm'>;

const MAX_SELECTION_TABLE_ROWS = 50;
const COPY_OVERRIDE_PAGE_SIZE = 20;
const COPY_OVERRIDE_SEARCH_DEBOUNCE_MS = 300;
const DRILL_HOLE_DIAMETER_MM = 3;
const DRILL_HOLE_RADIUS_MM = DRILL_HOLE_DIAMETER_MM / 2;
const DRILL_HOLE_EDGE_OFFSET_MM = 10;
const DRILL_HOLE_OUTLINE_WIDTH_MM = 0.2;

@Component({
  selector: 'app-sign-order-new',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    NzAlertComponent,
    NzButtonModule,
    NzCardModule,
    NzCheckboxModule,
    NzDividerModule,
    NzFormModule,
    NzIconModule,
    NzInputModule,
    NzInputNumberModule,
    NzModalModule,
    NzPaginationModule,
    NzRadioModule,
    NzSelectModule,
    NzSpinModule,
    NzStepsModule,
    NzTableModule,
    NzTagModule,
    NzToolTipModule,
    TranslateModule,
  ],
  templateUrl: './sign-order-new.component.html',
  styleUrls: ['./sign-order-new.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignOrderNewComponent implements OnInit, OnDestroy {
  readonly formatSignOrderDateTime = formatSignOrderDateTime;
  readonly formatSignOrderWeight = formatSignOrderWeight;

  @Output() navigateOrder = new EventEmitter<string>();
  @Output() navigateOrders = new EventEmitter<void>();
  @Output() draftSaved = new EventEmitter<string>();
  @Output() draftConsumed = new EventEmitter<void>();
  @Output() draftDeleted = new EventEmitter<void>();

  @Input()
  set draftPublicId(value: string | null) {
    const normalized = typeof value === 'string' && value.trim() ? value.trim() : null;
    if (normalized === this.requestedDraftPublicId) return;
    this.requestedDraftPublicId = normalized;
    if (!this.hasInitialized) return;
    if (!normalized && this.createdOrder) return;
    this.beginDraftSession();
  }

  @Input()
  set freshOrderSession(value: number) {
    if (!Number.isSafeInteger(value) || value === this.freshOrderSessionToken) return;
    this.freshOrderSessionToken = value;
    if (!this.hasInitialized) return;
    this.requestedDraftPublicId = null;
    this.beginDraftSession();
  }
  readonly STEP_PRODUCT = 0;
  readonly STEP_PROPERTIES = 1;
  readonly STEP_DRILLING = 2;
  readonly STEP_SHIPPING = 3;
  readonly STEP_REVIEW = 4;
  readonly STEP_CONFIRMATION = 5;

  currentStep: CheckoutStep = 0;
  highestReachedStep: VisibleCheckoutStep = this.STEP_PRODUCT;
  isLoading = false;
  isQuoteLoading = false;
  errorMessage: string | null = null;
  priceChangedMessage: string | null = null;

  catalog: CatalogResponse | null = null;
  product: CatalogProduct | null = null;
  showPriceTiers = false;
  presentationPricingTiers: readonly PricingTier[] = [];
  groupedPresentationPricingTiers: readonly DisplayPricingTier[] = [];
  priceSummary: PriceSummary | null = null;
  galleryActiveIndex = 0;
  bulkCopiesCount = 1;
  confirmBusiness = false;
  confirmContent = false;
  confirmSnapshot = false;
  confirmTerms = false;
  legalLinks: { label: string; url: string }[] = [];

  applyBulkCopies(): void {
    const count = this.bulkCopiesCount || 1;
    if (this.selection.selectAllData) {
      this.selection = {
        ...this.selection,
        selectAllData: {
          ...this.selection.selectAllData,
          defaultCopies: count,
          copyOverrides: {},
        },
      };
    } else {
      const copiesMap = Array.from(this.selection.selectedPropertyIds).reduce<Record<string, number>>((result, id) => {
        result[id] = count;
        return result;
      }, {});
      this.selection = { ...this.selection, copiesMap };
    }
    this.scheduleQuoteRefresh();
    this.refreshSelectionCache();
    this.refreshCopyOverrideResults();
  }

  propertySearch = '';
  propertyPage = 1;
  propertyPageSize = 10;
  propertyResults: PropertyItem[] = [];
  propertyTotal = 0;
  isSearching = false;
  minimumCopiesPerProperty = 1;
  maximumCopiesPerProperty = 99;
  copyOverrideModalVisible = false;
  copyOverrideSearch = '';
  copyOverridePage = 1;
  readonly copyOverridePageSize = COPY_OVERRIDE_PAGE_SIZE;
  copyOverrideResults: CopyOverridePropertyItem[] = [];
  copyOverrideTotal = 0;
  isCopyOverrideLoading = false;

  selection: SelectionState = {
    selectedPropertyIds: new Set(),
    copiesMap: {},
    selectAllData: null,
  };

  drillingOption: 'without_drilling' | 'with_drilling' = 'without_drilling';

  quote: QuoteResponse | null = null;
  quoteIsStale = false;
  quoteRefreshFailed = false;

  shippingForm!: FormGroup;
  billingForm!: FormGroup;
  billingSameAsShipping = true;

  isSubmitting = false;
  idempotencyKey = '';

  createdOrder: OrderDetailResponse | null = null;

  loadedDraft: SignOrderDraft | null = null;
  draftLoading = false;
  draftSaving = false;
  draftDeleting = false;
  draftUnavailable = false;
  draftLoadError: string | null = null;
  draftSaveError: string | null = null;
  draftSaveConfirmationVisible = false;
  reconciliationNoticeKeys: string[] = [];

  company: Company | null = null;
  imageBlobUrls: Record<string, string> = {};
  imageLoading: Record<string, boolean> = {};

  readonly MAX_SELECTION_TABLE_ROWS = MAX_SELECTION_TABLE_ROWS;
  cachedSelectedList: SelectedPropertyItem[] = [];
  cachedItemCount = 0;
  cachedCopyCount = 0;
  visibleSelectedPropertyIds: ReadonlySet<string> = new Set();
  visiblePropertyCopies: Readonly<Record<string, number>> = {};
  private manualPropertyMetadataById: Record<string, ManualPropertyMetadata> = {};
  private destroy$ = new Subject<void>();
  private quoteRefresh$ = new Subject<void>();
  private quoteRefreshSub: Subscription;
  private copyOverrideSearchDebounce?: ReturnType<typeof setTimeout>;
  private requestedDraftPublicId: string | null = null;
  private freshOrderSessionToken: number | null = null;
  private draftLoadGeneration = 0;
  private propertyListSession = 0;
  private propertyListRequest = 0;
  private hasRequestedInitialPropertyList = false;
  private hasInitialized = false;
  private initialShippingDraftValues: SignOrderDraftShippingAddress = {};
  private initialBillingDraftValues: SignOrderDraftBillingAddress = {};

  @ViewChild('posterPreviewCanvas') private posterPreviewCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('posterPreviewRenderCanvas') private posterPreviewRenderCanvas?: ElementRef<HTMLCanvasElement>;

  constructor(
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly message: NzMessageService,
    private readonly modal: NzModalService,
    public readonly translate: TranslateService,
    public readonly signOrderService: SignOrderService,
    private readonly qrPosterDocumentService: QrPosterDocumentService,
    private readonly qrPosterPdfPreviewService: QrPosterPdfPreviewService,
    private readonly authService: AuthService,
    private readonly companyService: CompanyService,
  ) {
    this.quoteRefreshSub = this.quoteRefresh$
      .pipe(
        debounceTime(500),
        switchMap(() => {
          if (this.getEffectiveItemCount() === 0 || !this.product) {
            this.quote = null;
            this.quoteIsStale = false;
            this.quoteRefreshFailed = false;
            this.cdr.markForCheck();
            return of(null);
          }
          this.isQuoteLoading = true;
          this.quoteRefreshFailed = false;
          this.cdr.markForCheck();
          const request = this.buildQuoteRequest();
          return this.signOrderService.requestQuote(request).pipe(
            tap((res: QuoteResponse) => {
              this.quote = res;
              this.isQuoteLoading = false;
              this.quoteIsStale = false;
              this.quoteRefreshFailed = false;
              this.priceChangedMessage = null;
              this.cdr.markForCheck();
            }),
            catchError(() => {
              this.isQuoteLoading = false;
              this.quoteRefreshFailed = true;
              this.message.error(this.translate.instant('SIGN_ORDER.CHECKOUT.ERROR_QUOTE'));
              this.cdr.markForCheck();
              return of(null);
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.buildShippingForm();
    this.loadInitialData();
  }

  private buildShippingForm(): void {
    this.shippingForm = this.fb.group({
      companyName: ['', [Validators.required, Validators.maxLength(160)]],
      contactName: ['', [Validators.required, Validators.maxLength(160)]],
      street: ['', [Validators.required, Validators.maxLength(160)]],
      houseNumber: ['', [Validators.required, Validators.maxLength(40)]],
      addressLine2: ['', Validators.maxLength(160)],
      postalCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      city: ['', [Validators.required, Validators.maxLength(120)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(254)]],
      phone: ['', Validators.maxLength(64)],
      countryCode: [{ value: 'DE', disabled: true }, Validators.required],
    });

    this.shippingForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.isSubmitting) {
        this.idempotencyKey = '';
        this.errorMessage = null;
      }
      this.invalidateReviewProgress();
    });

    this.billingForm = this.fb.group({
      companyName: ['', [Validators.required, Validators.maxLength(160)]],
      contactName: ['', [Validators.required, Validators.maxLength(160)]],
      street: ['', [Validators.required, Validators.maxLength(160)]],
      houseNumber: ['', [Validators.required, Validators.maxLength(40)]],
      addressLine2: ['', Validators.maxLength(160)],
      postalCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      city: ['', [Validators.required, Validators.maxLength(120)]],
      countryCode: [{ value: 'DE', disabled: true }, Validators.required],
    });
    this.billingForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.isSubmitting) {
        this.idempotencyKey = '';
        this.errorMessage = null;
      }
      this.invalidateReviewProgress();
    });
  }

  private loadInitialData(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.signOrderService.getCatalog().pipe(takeUntil(this.destroy$)).subscribe({
      next: (catalog) => {
        this.catalog = catalog;
        this.showPriceTiers = false;
        this.revokeAllImageBlobUrls();
        this.imageBlobUrls = {};
        this.galleryActiveIndex = 0;
        if (catalog.available && catalog.product) {
          this.product = catalog.product;
          this.setPresentationPricingTiers(catalog.product.pricingTiers);
          this.minimumCopiesPerProperty = catalog.product.minimumCopiesPerProperty || 1;
          this.maximumCopiesPerProperty = catalog.product.maximumCopiesPerProperty || 99;
          if (!catalog.product.allowedDrillingOptions.includes('with_drilling')) {
            this.drillingOption = 'without_drilling';
          }
          for (const image of catalog.product.images || []) {
            this.loadImage(image.url);
          }
        } else {
          this.product = null;
          this.setPresentationPricingTiers([]);
        }
        this.loadCompanyData();
        this.loadLegalManifest();
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = this.translate.instant('SIGN_ORDER.CHECKOUT.ERROR_LOADING');
        this.cdr.markForCheck();
      },
    });
  }

  private loadCompanyData(): void {
    this.companyService.getCompanyDetails().pipe(takeUntil(this.destroy$)).subscribe({
      next: (company) => {
        this.company = company;
        this.finishInitialLoad();
      },
      error: () => {
        this.finishInitialLoad();
      },
    });
  }

  private finishInitialLoad(): void {
    this.hasInitialized = true;
    if (this.requestedDraftPublicId) {
      this.beginDraftSession();
      return;
    }
    this.prefillShippingAddress();
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  private prefillShippingAddress(): void {
    if (!this.company) return;
    const company = this.company;
    const parts = company.address ? splitAddress(company.address) : { street: '', zipcode: '', city: '' };
    let streetName = parts.street || '';
    let houseNum = '';
    if (streetName) {
      const match = streetName.match(/^(.*)\s+(\d+\s*[a-zA-Z]?)$/);
      if (match) { streetName = match[1]; houseNum = match[2]; }
    }
    this.shippingForm.patchValue({
      companyName: company.name || '',
      street: streetName,
      houseNumber: houseNum,
      postalCode: parts.zipcode || '',
      city: parts.city || '',
      email: company.email || '',
      phone: company.phone || '',
      countryCode: 'DE',
    });
    this.initialShippingDraftValues = this.getDraftShippingAddress();
    this.initialBillingDraftValues = this.getDraftBillingAddress();
  }

  private beginDraftSession(): void {
    const requestId = ++this.draftLoadGeneration;
    this.resetWizardForDraftSession();

    if (!this.requestedDraftPublicId) {
      this.prefillShippingAddress();
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.draftLoading = true;
    this.signOrderService.getDraft(this.requestedDraftPublicId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (requestId !== this.draftLoadGeneration) return;
          this.loadedDraft = response.draft;
          this.draftLoading = false;

          if (!response.resume.productAvailable
            || !this.product
            || this.product.code.trim().toUpperCase() !== response.draft.productCode.trim().toUpperCase()) {
            this.draftUnavailable = true;
            this.isLoading = false;
            this.cdr.markForCheck();
            return;
          }

          this.restoreDraft(response);
        },
        error: () => {
          if (requestId !== this.draftLoadGeneration) return;
          this.draftLoading = false;
          this.draftLoadError = this.translate.instant('SIGN_ORDER.DRAFTS.ERROR_LOADING');
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private resetWizardForDraftSession(): void {
    const temporarySelectionId = this.selection.selectAllData?.selectionId;
    if (temporarySelectionId) {
      this.signOrderService.clearSelection(temporarySelectionId)
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }
    this.draftLoading = false;
    this.draftSaving = false;
    this.draftDeleting = false;
    this.draftUnavailable = false;
    this.draftLoadError = null;
    this.draftSaveError = null;
    this.draftSaveConfirmationVisible = false;
    this.reconciliationNoticeKeys = [];
    this.loadedDraft = null;
    this.showPriceTiers = false;
    this.selection = { selectedPropertyIds: new Set(), copiesMap: {}, selectAllData: null };
    this.manualPropertyMetadataById = {};
    this.propertyResults = [];
    this.propertyTotal = 0;
    this.propertySearch = '';
    this.propertyPage = 1;
    this.isSearching = false;
    this.propertyListSession += 1;
    this.hasRequestedInitialPropertyList = false;
    this.copyOverrideResults = [];
    this.copyOverrideTotal = 0;
    this.copyOverrideModalVisible = false;
    this.quote = null;
    this.quoteIsStale = false;
    this.quoteRefreshFailed = false;
    this.isQuoteLoading = false;
    this.priceChangedMessage = null;
    this.errorMessage = null;
    this.createdOrder = null;
    this.idempotencyKey = '';
    this.clearPosterPreview();
    this.confirmBusiness = false;
    this.confirmContent = false;
    this.confirmSnapshot = false;
    this.confirmTerms = false;
    this.drillingOption = this.getDefaultDrillingOption();
    this.shippingForm.reset({ countryCode: { value: 'DE', disabled: true } }, { emitEvent: false });
    this.shippingForm.markAsPristine();
    this.shippingForm.markAsUntouched();
    this.initialShippingDraftValues = {};
    this.billingForm.reset({ countryCode: { value: 'DE', disabled: true } }, { emitEvent: false });
    this.billingForm.markAsPristine();
    this.billingForm.markAsUntouched();
    this.billingSameAsShipping = true;
    this.initialBillingDraftValues = {};
    this.currentStep = this.STEP_PRODUCT;
    this.highestReachedStep = this.STEP_PRODUCT;
    this.refreshSelectionCache();
  }

  private restoreDraft(response: SignOrderDraftResumeResponse): void {
    const resume = response.resume;
    const metadata: Record<string, ManualPropertyMetadata> = {};
    const copiesMap: Record<string, number> = {};
    const selectedIds = new Set<string>();

    for (const item of resume.items || []) {
      if (!item?.propertyId || !Number.isSafeInteger(item.copies) || item.copies < 1) continue;
      selectedIds.add(item.propertyId);
      copiesMap[item.propertyId] = item.copies;
      if (item.property) {
        metadata[item.propertyId] = {
          id: item.property.id || item.propertyId,
          address: item.property.address || '',
          reference: item.property.reference || '',
        };
      }
    }

    this.selection = { selectedPropertyIds: selectedIds, copiesMap, selectAllData: null };
    this.manualPropertyMetadataById = metadata;
    this.refreshSelectionCache();
    this.drillingOption = resume.drillingOption && this.product?.allowedDrillingOptions.includes(resume.drillingOption)
      ? resume.drillingOption
      : this.getDefaultDrillingOption();
    this.shippingForm.patchValue({
      ...this.normalizeDraftShippingAddress(response.draft.shippingAddress),
      countryCode: 'DE',
    }, { emitEvent: false });
    this.shippingForm.markAsPristine();
    this.shippingForm.markAsUntouched();
    this.initialShippingDraftValues = this.getDraftShippingAddress();
    this.billingSameAsShipping = response.draft.billingSameAsShipping !== false;
    this.billingForm.patchValue({
      ...this.normalizeDraftBillingAddress(response.draft.billingAddress),
      countryCode: 'DE',
    }, { emitEvent: false });
    this.billingForm.markAsPristine();
    this.billingForm.markAsUntouched();
    this.initialBillingDraftValues = this.getDraftBillingAddress();
    this.applyDraftResumeStep(resume.resumeStep);
    this.setReconciliationNotices(response);
    this.draftLoading = false;
    this.isLoading = false;

    this.ensurePropertiesLoadedForCurrentStep();

    if (this.cachedItemCount > 0) {
      this.scheduleQuoteRefresh();
    }
    this.cdr.markForCheck();
  }

  private getDefaultDrillingOption(): 'without_drilling' | 'with_drilling' {
    if (this.product?.allowedDrillingOptions.includes('without_drilling')) return 'without_drilling';
    return this.product?.allowedDrillingOptions[0] || 'without_drilling';
  }

  private normalizeDraftShippingAddress(address: SignOrderDraftShippingAddress | undefined): SignOrderDraftShippingAddress {
    const source = address || {};
    return {
      companyName: typeof source.companyName === 'string' ? source.companyName : '',
      contactName: typeof source.contactName === 'string' ? source.contactName : '',
      street: typeof source.street === 'string' ? source.street : '',
      houseNumber: typeof source.houseNumber === 'string' ? source.houseNumber : '',
      addressLine2: typeof source.addressLine2 === 'string' ? source.addressLine2 : '',
      postalCode: typeof source.postalCode === 'string' ? source.postalCode : '',
      city: typeof source.city === 'string' ? source.city : '',
      email: typeof source.email === 'string' ? source.email : '',
      phone: typeof source.phone === 'string' ? source.phone : '',
    };
  }

  private normalizeDraftBillingAddress(address: SignOrderDraftBillingAddress | undefined): SignOrderDraftBillingAddress {
    const source = address || {};
    return {
      companyName: typeof source.companyName === 'string' ? source.companyName : '',
      contactName: typeof source.contactName === 'string' ? source.contactName : '',
      street: typeof source.street === 'string' ? source.street : '',
      houseNumber: typeof source.houseNumber === 'string' ? source.houseNumber : '',
      addressLine2: typeof source.addressLine2 === 'string' ? source.addressLine2 : '',
      postalCode: typeof source.postalCode === 'string' ? source.postalCode : '',
      city: typeof source.city === 'string' ? source.city : '',
      countryCode: 'DE',
    };
  }

  private applyDraftResumeStep(resumeStep: number | undefined): void {
    let safeStep: VisibleCheckoutStep = this.STEP_PRODUCT;
    if (this.cachedItemCount > 0) {
      safeStep = this.STEP_PROPERTIES;
      if (this.product?.allowedDrillingOptions.includes(this.drillingOption) && (resumeStep || 0) >= this.STEP_DRILLING) {
        safeStep = this.STEP_DRILLING;
      }
      if (safeStep === this.STEP_DRILLING && (resumeStep || 0) >= this.STEP_SHIPPING) {
        safeStep = this.STEP_SHIPPING;
      }
    }
    this.currentStep = safeStep;
    this.highestReachedStep = safeStep;
  }

  private setReconciliationNotices(response: SignOrderDraftResumeResponse): void {
    const reconciliation = response.reconciliation;
    const notices: string[] = [];
    if (response.resume.pricingRevisionChanged) notices.push('SIGN_ORDER.DRAFTS.RECONCILIATION_PRICING_CHANGED');
    if (reconciliation.unavailablePropertyIds?.length) notices.push('SIGN_ORDER.DRAFTS.RECONCILIATION_PROPERTIES_REMOVED');
    if (reconciliation.copyAdjustments?.length) notices.push('SIGN_ORDER.DRAFTS.RECONCILIATION_COPIES_ADJUSTED');
    if (reconciliation.drillingOptionChanged) notices.push('SIGN_ORDER.DRAFTS.RECONCILIATION_DRILLING_CHANGED');
    if (reconciliation.resumeStepChanged) notices.push('SIGN_ORDER.DRAFTS.RECONCILIATION_STEP_LOWERED');
    if (response.resume.totalQuantityExceedsMaximum) notices.push('SIGN_ORDER.DRAFTS.RECONCILIATION_TOTAL_EXCEEDS_MAXIMUM');
    this.reconciliationNoticeKeys = notices;
  }

  goToStep(step: CheckoutStep): void {
    if (!this.isWizardEditable() || !this.isVisibleCheckoutStep(step) || step === this.currentStep) return;

    if (step < this.currentStep) {
      this.activateStep(step);
      return;
    }

    if (step !== this.currentStep + 1 || !this.canAdvanceToStep(step)) return;

    this.highestReachedStep = Math.max(this.highestReachedStep, step) as VisibleCheckoutStep;
    this.activateStep(step);
  }

  onStepHeaderIndexChange(index: number): void {
    this.activateReachedStep(index);
  }

  onStepHeaderKeydown(event: KeyboardEvent): void {
    const isEnter = event.key === 'Enter';
    const isSpace = event.key === ' ' || event.code === 'Space';
    if (!isEnter && !isSpace) return;

    const host = event.currentTarget;
    const target = event.target;
    if (!(host instanceof HTMLElement) || !(target instanceof Element)) return;

    const stepElement = target.closest<HTMLElement>('.ant-steps-item');
    if (!stepElement || !host.contains(stepElement)) return;

    const index = Array.from(host.querySelectorAll<HTMLElement>('.ant-steps-item')).indexOf(stepElement);
    if (index < 0 || !this.activateReachedStep(index)) return;

    if (isSpace) {
      event.preventDefault();
    }
  }

  isStepHeaderDisabled(step: number): boolean {
    if (!this.isWizardEditable() || !this.isVisibleCheckoutStep(step)) return true;
    if (step > this.highestReachedStep) return true;
    return step === this.STEP_REVIEW && !this.canProceedToReview();
  }

  canProceedToReview(): boolean {
    const hasItems = this.getEffectiveItemCount() > 0;
    const hasAddress = this.shippingForm.valid && this.isBillingAddressValid();
    return hasItems && hasAddress && Boolean(this.quote) && !this.quoteIsStale && !this.isQuoteLoading;
  }

  private activateReachedStep(index: number): boolean {
    if (!this.isVisibleCheckoutStep(index) || index === this.currentStep || this.isStepHeaderDisabled(index)) {
      return false;
    }
    this.activateStep(index);
    return true;
  }

  private canAdvanceToStep(step: VisibleCheckoutStep): boolean {
    if (step >= this.STEP_DRILLING && this.totalQuantityExceedsMaximum) return false;
    if (step === this.STEP_PROPERTIES) return Boolean(this.product);
    if (step === this.STEP_DRILLING) return this.getEffectiveItemCount() > 0;
    if (step === this.STEP_SHIPPING) return Boolean(this.product?.allowedDrillingOptions?.includes(this.drillingOption));
    if (step === this.STEP_REVIEW) {
      if (!this.canProceedToReview()) {
        this.shippingForm.markAllAsTouched();
        if (!this.billingSameAsShipping) this.billingForm.markAllAsTouched();
        return false;
      }
      return true;
    }
    return false;
  }

  private activateStep(step: VisibleCheckoutStep): void {
    this.currentStep = step;
    this.ensurePropertiesLoadedForCurrentStep();
    this.errorMessage = null;
    this.priceChangedMessage = null;
    if (step === this.STEP_REVIEW) {
      this.loadPosterPreview();
    }
    this.cdr.markForCheck();
  }

  private isWizardEditable(): boolean {
    return this.currentStep !== this.STEP_CONFIRMATION && !this.createdOrder;
  }

  private isVisibleCheckoutStep(step: number): step is VisibleCheckoutStep {
    return Number.isInteger(step) && step >= this.STEP_PRODUCT && step <= this.STEP_REVIEW;
  }

  private capHighestReachedStep(step: VisibleCheckoutStep): void {
    if (this.highestReachedStep > step) {
      this.highestReachedStep = step;
    }
  }

  private invalidateReviewProgress(): void {
    this.capHighestReachedStep(this.STEP_SHIPPING);
  }

  getEffectiveItemCount(): number {
    return this.cachedItemCount;
  }

  getEffectiveCopyCount(): number {
    return this.cachedCopyCount;
  }

  get totalQuantityExceedsMaximum(): boolean {
    const maximum = this.product?.maximumTotalQuantity;
    return typeof maximum === 'number' && Number.isSafeInteger(maximum) && this.cachedCopyCount > maximum;
  }

  getUniquePropertyCount(): number {
    return this.cachedItemCount;
  }

  private refreshSelectionCache(recalculateTotals = true): void {
    if (recalculateTotals) {
      this.refreshSelectionTotals();
    }
    this.refreshCachedSelectedList();
    this.refreshVisiblePropertySelection();
    this.cdr.markForCheck();
  }

  private refreshSelectionTotals(): void {
    if (this.selection.selectAllData) {
      this.cachedItemCount = Math.max(0, this.selection.selectAllData.total - this.selection.selectAllData.excludedIds.size);
      const defaultCopies = this.selection.selectAllData.defaultCopies;
      let copyCount = this.cachedItemCount * defaultCopies;
      for (const id of Object.keys(this.selection.selectAllData.copyOverrides)) {
        if (!this.selection.selectAllData.excludedIds.has(id)) {
          copyCount += (this.selection.selectAllData.copyOverrides[id] || defaultCopies) - defaultCopies;
        }
      }
      this.cachedCopyCount = Math.max(0, copyCount);
      return;
    }

    this.cachedItemCount = this.selection.selectedPropertyIds.size;
    let copyCount = 0;
    this.selection.selectedPropertyIds.forEach((id) => { copyCount += this.selection.copiesMap[id] || 1; });
    this.cachedCopyCount = copyCount;
  }

  private refreshCachedSelectedList(): void {
    const selectAllData = this.selection.selectAllData;
    const propertyIds = selectAllData?.excludedIds || this.selection.selectedPropertyIds;
    const isExcluded = Boolean(selectAllData);
    const cachedProperties: SelectedPropertyItem[] = [];

    for (const id of propertyIds) {
      if (cachedProperties.length > MAX_SELECTION_TABLE_ROWS) {
        break;
      }
      const property = selectAllData
        ? this.propertyResults.find((item) => item.id === id)
        : this.manualPropertyMetadataById[id];
      cachedProperties.push({
        id,
        address: property?.address || '',
        reference: property?.reference || '',
        _excluded: isExcluded,
        copies: this.getPropertyCopies(id),
      });
    }

    this.cachedSelectedList = cachedProperties;
  }

  private refreshVisiblePropertySelection(): void {
    const selectedPropertyIds = new Set<string>();
    const propertyCopies: Record<string, number> = {};
    const selectAllData = this.selection.selectAllData;

    for (const property of this.propertyResults) {
      const isSelected = selectAllData
        ? !selectAllData.excludedIds.has(property.id)
        : this.selection.selectedPropertyIds.has(property.id);
      if (!isSelected) {
        continue;
      }
      selectedPropertyIds.add(property.id);
      propertyCopies[property.id] = this.getPropertyCopies(property.id);
    }

    this.visibleSelectedPropertyIds = selectedPropertyIds;
    this.visiblePropertyCopies = propertyCopies;
  }

  private refreshCopyOverrideResults(): void {
    if (!this.copyOverrideModalVisible || this.copyOverrideResults.length === 0) {
      return;
    }
    if (this.selection.selectAllData) {
      this.copyOverrideResults = this.copyOverrideResults.map((property) => this.toCopyOverrideProperty(property));
      return;
    }

    this.copyOverrideResults = this.copyOverrideResults.map((property) => {
      const selected = this.selection.selectedPropertyIds.has(property.id);
      const metadata = this.manualPropertyMetadataById[property.id];
      return {
        ...property,
        address: metadata?.address ?? property.address,
        reference: metadata?.reference ?? property.reference,
        copies: selected ? this.getPropertyCopies(property.id) : property.copies,
        isExcluded: !selected,
        hasCopyOverride: false,
      };
    });
  }

  private toCopyOverrideProperty(property: PropertyItem): CopyOverridePropertyItem {
    const selectAllData = this.selection.selectAllData;
    const isExcluded = Boolean(selectAllData?.excludedIds.has(property.id));
    return {
      ...property,
      copies: this.getPropertyCopies(property.id),
      isExcluded,
      hasCopyOverride: Boolean(selectAllData && Object.prototype.hasOwnProperty.call(selectAllData.copyOverrides, property.id)),
    };
  }

  private hydrateManualPropertyMetadata(properties: readonly PropertyItem[]): void {
    if (this.selection.selectAllData) {
      return;
    }

    const metadata = { ...this.manualPropertyMetadataById };
    for (const property of properties) {
      const propertyId = String(property?.id || '').trim();
      if (!propertyId || !this.selection.selectedPropertyIds.has(propertyId)) {
        continue;
      }
      metadata[propertyId] = {
        id: propertyId,
        address: typeof property.address === 'string' ? property.address.trim() : '',
        reference: typeof property.reference === 'string' ? property.reference.trim() : '',
      };
    }
    this.manualPropertyMetadataById = metadata;
  }

  private hydrateManualPropertyMetadataForId(propertyId: string): void {
    const property = this.propertyResults.find((item) => item.id === propertyId)
      || this.copyOverrideResults.find((item) => item.id === propertyId);
    if (property) {
      this.hydrateManualPropertyMetadata([property]);
    }
  }

  onPropertySearchChange(): void {
    this.propertyPage = 1;
    if (this._searchDebounce) clearTimeout(this._searchDebounce);
    this._searchDebounce = setTimeout(() => this.searchProperties(), 400);
  }

  onSearchFocus(): void {
    if (this.propertyResults.length === 0) {
      this.searchProperties();
    }
  }

  private ensurePropertiesLoadedForCurrentStep(): void {
    if (this.currentStep !== this.STEP_PROPERTIES
      || !this.product
      || this.draftUnavailable
      || this.isSearching
      || this.hasRequestedInitialPropertyList
      || this.propertyResults.length > 0) {
      return;
    }
    this.searchProperties();
  }

  searchProperties(page?: number): void {
    if (page) this.propertyPage = page;
    const requestId = ++this.propertyListRequest;
    const session = this.propertyListSession;
    const search = this.propertySearch;
    const requestedPage = this.propertyPage;
    this.hasRequestedInitialPropertyList = true;
    this.isSearching = true;
    this.cdr.markForCheck();
    this.signOrderService
      .searchProperties(search, requestedPage, this.propertyPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (requestId !== this.propertyListRequest || session !== this.propertyListSession) return;
          this.propertyResults = res.items;
          this.propertyTotal = res.total;
          this.isSearching = false;
          this.hydrateManualPropertyMetadata(res.items);
          this.refreshSelectionCache(false);
          this.refreshCopyOverrideResults();
          this.cdr.markForCheck();
        },
        error: () => {
          if (requestId !== this.propertyListRequest || session !== this.propertyListSession) return;
          this.isSearching = false;
          this.message.error(this.translate.instant('SIGN_ORDER.CHECKOUT.ERROR_SEARCHING'));
          this.cdr.markForCheck();
        },
      });
  }

  openCopyOverrideModal(): void {
    this.copyOverrideModalVisible = true;
    this.copyOverrideSearch = '';
    this.copyOverridePage = 1;
    this.loadCopyOverrideProperties();
  }

  closeCopyOverrideModal(): void {
    this.copyOverrideModalVisible = false;
    this.isCopyOverrideLoading = false;
    if (this.copyOverrideSearchDebounce) {
      clearTimeout(this.copyOverrideSearchDebounce);
      this.copyOverrideSearchDebounce = undefined;
    }
  }

  onCopyOverrideSearchChange(): void {
    this.copyOverridePage = 1;
    if (this.copyOverrideSearchDebounce) {
      clearTimeout(this.copyOverrideSearchDebounce);
    }
    this.copyOverrideSearchDebounce = setTimeout(() => {
      this.copyOverrideSearchDebounce = undefined;
      this.loadCopyOverrideProperties();
    }, COPY_OVERRIDE_SEARCH_DEBOUNCE_MS);
  }

  loadCopyOverrideProperties(page?: number): void {
    const selectAllData = this.selection.selectAllData;
    if (!this.copyOverrideModalVisible) {
      return;
    }
    if (page) {
      this.copyOverridePage = page;
    }
    if (!selectAllData) {
      this.loadManualCopyOverrideProperties();
      return;
    }
    const selectionId = selectAllData.selectionId;
    const search = this.copyOverrideSearch;
    const requestedPage = this.copyOverridePage;
    this.isCopyOverrideLoading = true;
    this.cdr.markForCheck();

    this.signOrderService
      .searchSelectionProperties(selectionId, search, requestedPage, this.copyOverridePageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (
            this.selection.selectAllData?.selectionId !== selectionId
            || !this.copyOverrideModalVisible
            || this.copyOverrideSearch !== search
            || this.copyOverridePage !== requestedPage
          ) {
            return;
          }
          this.copyOverrideResults = res.items.map((property) => this.toCopyOverrideProperty(property));
          this.copyOverrideTotal = res.total;
          this.isCopyOverrideLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          if (
            this.selection.selectAllData?.selectionId !== selectionId
            || !this.copyOverrideModalVisible
            || this.copyOverrideSearch !== search
            || this.copyOverridePage !== requestedPage
          ) {
            return;
          }
          this.isCopyOverrideLoading = false;
          this.message.error(this.translate.instant('SIGN_ORDER.CHECKOUT.ERROR_SEARCHING'));
          this.cdr.markForCheck();
        },
      });
  }

  private loadManualCopyOverrideProperties(): void {
    const search = this.copyOverrideSearch.trim().toLocaleLowerCase();
    const selectedProperties = Array.from(this.selection.selectedPropertyIds).map((id) => {
      const metadata = this.manualPropertyMetadataById[id];
      return {
        id,
        address: metadata?.address || '',
        reference: metadata?.reference || '',
        copies: this.getPropertyCopies(id),
        isExcluded: false,
        hasCopyOverride: false,
      };
    });
    const filteredProperties = search
      ? selectedProperties.filter((property) => property.address.toLocaleLowerCase().includes(search))
      : selectedProperties;
    const lastPage = Math.max(1, Math.ceil(filteredProperties.length / this.copyOverridePageSize));
    this.copyOverridePage = Math.min(this.copyOverridePage, lastPage);
    const start = (this.copyOverridePage - 1) * this.copyOverridePageSize;
    this.copyOverrideResults = filteredProperties.slice(start, start + this.copyOverridePageSize);
    this.copyOverrideTotal = filteredProperties.length;
    this.isCopyOverrideLoading = false;
    this.cdr.markForCheck();
  }

  private _searchDebounce: any;

  toggleProperty(propertyId: string): void {
    const wasSelected = this.selection.selectAllData
      ? !this.selection.selectAllData.excludedIds.has(propertyId)
      : this.selection.selectedPropertyIds.has(propertyId);
    const propertyCopies = this.getPropertyCopies(propertyId);

    if (this.selection.selectAllData) {
      const excluded = new Set(this.selection.selectAllData.excludedIds);
      const copyOverrides = { ...this.selection.selectAllData.copyOverrides };
      if (excluded.has(propertyId)) {
        excluded.delete(propertyId);
      } else {
        excluded.add(propertyId);
        delete copyOverrides[propertyId];
      }
      this.selection = {
        ...this.selection,
        selectAllData: {
          ...this.selection.selectAllData,
          excludedIds: excluded,
          copyOverrides,
        },
      };
    } else {
      const selected = new Set(this.selection.selectedPropertyIds);
      if (selected.has(propertyId)) {
        selected.delete(propertyId);
        const copiesMap = { ...this.selection.copiesMap };
        delete copiesMap[propertyId];
        this.selection = { ...this.selection, selectedPropertyIds: selected, copiesMap };
        const metadata = { ...this.manualPropertyMetadataById };
        delete metadata[propertyId];
        this.manualPropertyMetadataById = metadata;
      } else {
        selected.add(propertyId);
        this.selection = { ...this.selection, selectedPropertyIds: selected, copiesMap: { ...this.selection.copiesMap, [propertyId]: 1 } };
        this.hydrateManualPropertyMetadataForId(propertyId);
      }
    }
    this.cachedItemCount += wasSelected ? -1 : 1;
    this.cachedCopyCount = Math.max(0, this.cachedCopyCount + (wasSelected ? -propertyCopies : propertyCopies));
    this.scheduleQuoteRefresh();
    this.refreshSelectionCache(false);
    if (this.cachedItemCount === 0) {
      this.capHighestReachedStep(this.STEP_PROPERTIES);
    }
    this.refreshCopyOverrideResults();
    this.invalidatePosterPreview();
  }

  getPropertyCopies(propertyId: string): number {
    if (this.selection.selectAllData) {
      return this.selection.selectAllData.copyOverrides[propertyId] ?? this.selection.selectAllData.defaultCopies;
    }
    return this.selection.copiesMap[propertyId] || 1;
  }

  setPropertyCopies(propertyId: string, copies: number): void {
    const min = this.minimumCopiesPerProperty;
    const max = this.maximumCopiesPerProperty;
    const clamped = Math.max(min, Math.min(max, copies || min));
    const previousCopies = this.getPropertyCopies(propertyId);
    const isSelected = this.selection.selectAllData
      ? !this.selection.selectAllData.excludedIds.has(propertyId)
      : this.selection.selectedPropertyIds.has(propertyId);

    if (!isSelected) {
      return;
    }

    if (this.selection.selectAllData) {
      const copyOverrides = { ...this.selection.selectAllData.copyOverrides };
      if (clamped === this.selection.selectAllData.defaultCopies) {
        delete copyOverrides[propertyId];
      } else {
        copyOverrides[propertyId] = clamped;
      }
      this.selection = {
        ...this.selection,
        selectAllData: {
          ...this.selection.selectAllData,
          copyOverrides,
        },
      };
    } else {
      this.selection = {
        ...this.selection,
        copiesMap: { ...this.selection.copiesMap, [propertyId]: clamped },
      };
    }
    this.scheduleQuoteRefresh();
    if (isSelected) {
      this.cachedCopyCount = Math.max(0, this.cachedCopyCount + clamped - previousCopies);
    }
    this.refreshSelectionCache(false);
    this.refreshCopyOverrideResults();
    this.invalidatePosterPreview();
  }

  resetCopyOverride(propertyId: string): void {
    const selectAllData = this.selection.selectAllData;
    if (!selectAllData || selectAllData.excludedIds.has(propertyId)) {
      return;
    }
    this.setPropertyCopies(propertyId, selectAllData.defaultCopies);
  }

  selectAllMatching(): void {
    if (this.selection.selectAllData) return;

    this.errorMessage = null;
    this.isLoading = true;

    this.signOrderService.selectAll(this.propertySearch).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: SelectAllResponse) => {
        this.manualPropertyMetadataById = {};
        this.copyOverrideResults = [];
        this.copyOverrideTotal = 0;
        this.selection = {
          selectedPropertyIds: new Set(),
          copiesMap: {},
          selectAllData: {
            selectionId: res.selectionId,
            total: res.total,
            search: this.propertySearch,
            excludedIds: new Set(),
            defaultCopies: 1,
            copyOverrides: {},
          },
        };
        this.isLoading = false;
        this.scheduleQuoteRefresh();
        this.refreshSelectionCache();
        if (this.cachedItemCount === 0) {
          this.capHighestReachedStep(this.STEP_PROPERTIES);
        }
        if (this.copyOverrideModalVisible) {
          this.copyOverrideSearch = '';
          this.copyOverridePage = 1;
          this.loadCopyOverrideProperties();
        }
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading = false;
        if (err.status === 409 && err.error?.code === 'SIGN_ORDER_SELECTION_EMPTY') {
          this.message.warning(this.translate.instant('SIGN_ORDER.CHECKOUT.SELECT_ALL_EMPTY'));
        } else {
          this.message.error(this.translate.instant('SIGN_ORDER.CHECKOUT.ERROR_SELECT_ALL'));
        }
        this.cdr.markForCheck();
      },
    });
  }

  clearSelection(): void {
    if (this.selection.selectAllData?.selectionId) {
      this.signOrderService.clearSelection(this.selection.selectAllData.selectionId)
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }
    this.selection = {
      selectedPropertyIds: new Set(),
      copiesMap: {},
      selectAllData: null,
    };
    this.manualPropertyMetadataById = {};
    this.quote = null;
    this.quoteIsStale = false;
    this.quoteRefreshFailed = false;
    this.priceChangedMessage = null;
    this.idempotencyKey = '';
    this.errorMessage = null;
    this.refreshSelectionCache();
    this.capHighestReachedStep(this.STEP_PROPERTIES);
    this.closeCopyOverrideModal();
    this.copyOverrideResults = [];
    this.copyOverrideTotal = 0;
  }

  scheduleQuoteRefresh(): void {
    this.invalidateReviewProgress();
    this.quoteIsStale = true;
    this.quoteRefreshFailed = false;
    this.priceChangedMessage = null;
    this.quoteRefresh$.next();
    if (!this.isSubmitting) {
      this.resetIdempotencyKey();
    }
  }

  selectDrillingOption(option: 'without_drilling' | 'with_drilling'): void {
    if (this.drillingOption === option) return;

    this.drillingOption = option;
    this.scheduleQuoteRefresh();
    this.invalidatePosterPreview();
  }

  get canSaveDraft(): boolean {
    return this.isWizardEditable()
      && !this.draftUnavailable
      && !this.draftSaving
      && Boolean(this.product)
      && this.currentStep >= this.STEP_PROPERTIES
      && this.currentStep <= this.STEP_REVIEW
      && this.hasDraftMeaningfulProgress();
  }

  get shouldShowDraftActions(): boolean {
    return this.isWizardEditable()
      && !this.draftUnavailable
      && this.currentStep >= this.STEP_PROPERTIES
      && this.currentStep <= this.STEP_REVIEW;
  }

  private hasDraftMeaningfulProgress(): boolean {
    if (this.cachedItemCount > 0 || this.drillingOption !== this.getDefaultDrillingOption()) return true;
    const currentShipping = this.getDraftShippingAddress();
    const currentBilling = this.getDraftBillingAddress();
    if (this.loadedDraft) return Object.keys(currentShipping).length > 0 || !this.billingSameAsShipping || this.hasMeaningfulBillingAddress(currentBilling);
    return Object.entries(currentShipping).some(([key, value]) => value !== (this.initialShippingDraftValues[key as keyof SignOrderDraftShippingAddress] || ''))
      || !this.billingSameAsShipping
      || this.hasBillingDraftValuesChanged(currentBilling);
  }

  private getDraftShippingAddress(): SignOrderDraftShippingAddress {
    const fields: Array<keyof SignOrderDraftShippingAddress> = [
      'companyName', 'contactName', 'street', 'houseNumber', 'addressLine2',
      'postalCode', 'city', 'email', 'phone',
    ];
    return fields.reduce<SignOrderDraftShippingAddress>((address, field) => {
      const raw = this.shippingForm.get(field)?.value;
      if (typeof raw === 'string' && raw.trim()) {
        address[field] = raw.trim();
      }
      return address;
    }, {});
  }

  private getDraftBillingAddress(): SignOrderDraftBillingAddress {
    const fields: Array<keyof SignOrderDraftBillingAddress> = [
      'companyName', 'contactName', 'street', 'houseNumber', 'addressLine2', 'postalCode', 'city', 'countryCode',
    ];
    return fields.reduce<SignOrderDraftBillingAddress>((address, field) => {
      const raw = this.billingForm.get(field)?.value;
      if (typeof raw === 'string' && raw.trim()) address[field] = raw.trim();
      return address;
    }, {});
  }

  private hasMeaningfulBillingAddress(address: SignOrderDraftBillingAddress): boolean {
    return ['companyName', 'contactName', 'street', 'houseNumber', 'addressLine2', 'postalCode', 'city']
      .some((field) => {
        const value = address[field as keyof SignOrderDraftBillingAddress];
        return typeof value === 'string' && value.trim().length > 0;
      });
  }

  private hasBillingDraftValuesChanged(currentBilling: SignOrderDraftBillingAddress): boolean {
    return ['companyName', 'contactName', 'street', 'houseNumber', 'addressLine2', 'postalCode', 'city']
      .some((field) => currentBilling[field as keyof SignOrderDraftBillingAddress] !== this.initialBillingDraftValues[field as keyof SignOrderDraftBillingAddress]);
  }

  isBillingAddressValid(): boolean {
    return this.billingSameAsShipping || this.billingForm.valid;
  }

  onBillingSameAsShippingChange(value: boolean): void {
    this.billingSameAsShipping = value;
    this.idempotencyKey = '';
    this.errorMessage = null;
    this.invalidateReviewProgress();
  }

  private buildDraftRequest(): CreateSignOrderDraftRequest {
    const request: CreateSignOrderDraftRequest = {
      productCode: this.product!.code,
      drillingOption: this.drillingOption,
      shippingAddress: this.getDraftShippingAddress(),
      billingSameAsShipping: this.billingSameAsShipping,
      billingAddress: this.getDraftBillingAddress(),
      resumeStep: Math.min(this.currentStep, this.STEP_SHIPPING),
    };

    if (this.selection.selectAllData) {
      request.selectionId = this.selection.selectAllData.selectionId;
      if (this.selection.selectAllData.excludedIds.size > 0) {
        request.excludedPropertyIds = Array.from(this.selection.selectAllData.excludedIds);
      }
      if (this.selection.selectAllData.defaultCopies !== 1) {
        request.defaultCopies = this.selection.selectAllData.defaultCopies;
      }
      if (Object.keys(this.selection.selectAllData.copyOverrides).length > 0) {
        request.copyOverrides = { ...this.selection.selectAllData.copyOverrides };
      }
    } else if (this.selection.selectedPropertyIds.size > 0) {
      request.items = Array.from(this.selection.selectedPropertyIds).map((id) => ({
        propertyId: id,
        copies: this.selection.copiesMap[id] || 1,
      }));
    }

    return request;
  }

  saveDraft(): void {
    if (!this.canSaveDraft) return;

    const request = this.buildDraftRequest();
    this.draftSaving = true;
    this.draftSaveError = null;
    this.draftSaveConfirmationVisible = false;
    const saveRequest = this.loadedDraft
      ? this.signOrderService.updateDraft(this.loadedDraft.publicId, { ...request, revision: this.loadedDraft.revision } as UpdateSignOrderDraftRequest)
      : this.signOrderService.createDraft(request);

    saveRequest.pipe(takeUntil(this.destroy$)).subscribe({
      next: (draft) => {
        this.loadedDraft = draft;
        this.requestedDraftPublicId = draft.publicId;
        this.draftSaving = false;
        this.initialShippingDraftValues = this.getDraftShippingAddress();
        this.initialBillingDraftValues = this.getDraftBillingAddress();
        this.draftSaveConfirmationVisible = true;
        this.draftSaved.emit(draft.publicId);
        this.cdr.markForCheck();
      },
      error: (error: HttpErrorResponse) => {
        this.draftSaving = false;
        const message = error.error?.code === 'DRAFT_VERSION_CONFLICT'
          ? this.translate.instant('SIGN_ORDER.DRAFTS.VERSION_CONFLICT_SAVE')
          : this.translate.instant('SIGN_ORDER.DRAFTS.ERROR_SAVE');
        this.draftSaveError = message;
        this.message.error(message);
        this.cdr.markForCheck();
      },
    });
  }

  viewSavedDrafts(): void {
    this.dismissDraftSaveConfirmation();
    this.navigateOrders.emit();
  }

  dismissDraftSaveConfirmation(): void {
    this.draftSaveConfirmationVisible = false;
    this.cdr.markForCheck();
  }

  confirmDeleteDraft(): void {
    if (!this.loadedDraft || this.draftDeleting) return;
    this.modal.confirm({
      nzTitle: this.translate.instant('SIGN_ORDER.DRAFTS.DELETE_CONFIRMATION_TITLE'),
      nzContent: this.translate.instant('SIGN_ORDER.DRAFTS.DELETE_CONFIRMATION_TEXT'),
      nzOkText: this.translate.instant('SIGN_ORDER.DRAFTS.DELETE'),
      nzOkDanger: true,
      nzCancelText: this.translate.instant('COMMON.CANCEL'),
      nzOnOk: () => this.deleteLoadedDraft(),
    });
  }

  private deleteLoadedDraft(): Promise<void> {
    if (!this.loadedDraft) return Promise.resolve();
    this.draftDeleting = true;
    const draft = this.loadedDraft;
    return new Promise((resolve) => {
      this.signOrderService.deleteDraft(draft.publicId, draft.revision)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.draftDeleting = false;
            this.loadedDraft = null;
            this.requestedDraftPublicId = null;
            this.draftSaveConfirmationVisible = false;
            this.message.success(this.translate.instant('SIGN_ORDER.DRAFTS.DELETED'));
            this.draftDeleted.emit();
            resolve();
          },
          error: (error: HttpErrorResponse) => {
            this.draftDeleting = false;
            const message = error.error?.code === 'DRAFT_VERSION_CONFLICT'
              ? this.translate.instant('SIGN_ORDER.DRAFTS.VERSION_CONFLICT_LIST')
              : this.translate.instant('SIGN_ORDER.DRAFTS.ERROR_DELETE');
            this.message.error(message);
            resolve();
          },
        });
    });
  }

  private buildQuoteRequest(): QuoteRequest {
    const request: QuoteRequest = {
      drillingOption: this.drillingOption,
      locale: this.translate.currentLang || 'de',
    };

    if (this.selection.selectAllData) {
      request.selectionId = this.selection.selectAllData.selectionId;
      if (this.selection.selectAllData.excludedIds.size > 0) {
        request.excludedPropertyIds = Array.from(this.selection.selectAllData.excludedIds);
      }
      if (this.selection.selectAllData.defaultCopies !== 1) {
        request.defaultCopies = this.selection.selectAllData.defaultCopies;
      }
      if (Object.keys(this.selection.selectAllData.copyOverrides).length > 0) {
        request.copyOverrides = { ...this.selection.selectAllData.copyOverrides };
      }
    } else {
      request.items = Array.from(this.selection.selectedPropertyIds).map((id) => ({
        propertyId: id,
        copies: this.selection.copiesMap[id] || 1,
      }));
    }

    return request;
  }

  private buildOrderRequest(): CreateOrderRequest {
    const idempotencyKey = this.idempotencyKey || this.generateIdempotencyKey();
    this.idempotencyKey = idempotencyKey;

    const address: ShippingAddress = {
      companyName: this.shippingForm.get('companyName')?.value || '',
      contactName: this.shippingForm.get('contactName')?.value || '',
      street: this.shippingForm.get('street')?.value || '',
      houseNumber: this.shippingForm.get('houseNumber')?.value || '',
      addressLine2: this.shippingForm.get('addressLine2')?.value || '',
      postalCode: this.shippingForm.get('postalCode')?.value || '',
      city: this.shippingForm.get('city')?.value || '',
      countryCode: 'DE',
      email: this.shippingForm.get('email')?.value || '',
      phone: this.shippingForm.get('phone')?.value || '',
    };

    const billingAddress: BillingAddress = {
      companyName: this.billingForm.get('companyName')?.value || '',
      contactName: this.billingForm.get('contactName')?.value || '',
      street: this.billingForm.get('street')?.value || '',
      houseNumber: this.billingForm.get('houseNumber')?.value || '',
      addressLine2: this.billingForm.get('addressLine2')?.value || '',
      postalCode: this.billingForm.get('postalCode')?.value || '',
      city: this.billingForm.get('city')?.value || '',
      countryCode: 'DE',
    };

    const request: CreateOrderRequest = {
      drillingOption: this.drillingOption,
      shippingAddress: address,
      billingSameAsShipping: this.billingSameAsShipping,
      ...(!this.billingSameAsShipping ? { billingAddress } : {}),
      locale: this.translate.currentLang || 'de',
      productPricingRevision: this.quote!.productPricingRevision,
      settingsPricingRevision: this.quote!.settingsPricingRevision,
      idempotencyKey,
    };

    if (this.loadedDraft?.publicId) {
      request.draftPublicId = this.loadedDraft.publicId;
    }

    (request as any).legal = {
      businessOrderConfirmed: this.confirmBusiness,
      orderingAuthorityConfirmed: this.confirmBusiness,
      contentRightsConfirmed: this.confirmContent,
      printSnapshotApproved: this.confirmSnapshot,
      signOrderTermsAccepted: this.confirmTerms,
      generalTermsAccepted: this.confirmTerms,
    };

    if (this.posterPreviewRevision) {
      request.posterPreviewRevision = this.posterPreviewRevision;
    }

    if (this.selection.selectAllData) {
      request.selectionId = this.selection.selectAllData.selectionId;
      if (this.selection.selectAllData.excludedIds.size > 0) {
        request.excludedPropertyIds = Array.from(this.selection.selectAllData.excludedIds);
      }
      if (this.selection.selectAllData.defaultCopies !== 1) {
        request.defaultCopies = this.selection.selectAllData.defaultCopies;
      }
      if (Object.keys(this.selection.selectAllData.copyOverrides).length > 0) {
        request.copyOverrides = { ...this.selection.selectAllData.copyOverrides };
      }
    } else {
      request.items = Array.from(this.selection.selectedPropertyIds).map((id) => ({
        propertyId: id,
        copies: this.selection.copiesMap[id] || 1,
      }));
    }

    return request;
  }

  submitOrder(): void {
    if (this.isSubmitting || this.quoteIsStale || !this.quote) return;

    this.shippingForm.markAllAsTouched();
    if (!this.billingSameAsShipping) this.billingForm.markAllAsTouched();
    if (this.shippingForm.invalid || !this.isBillingAddressValid()) return;

    this.isSubmitting = true;
    this.errorMessage = null;
    this.priceChangedMessage = null;

    const request = this.buildOrderRequest();

    this.signOrderService.createOrder(request).pipe(takeUntil(this.destroy$)).subscribe({
      next: (order) => {
        this.isSubmitting = false;
        this.createdOrder = order;
        const consumedDraft = this.loadedDraft;
        this.loadedDraft = null;
        this.requestedDraftPublicId = null;
        this.currentStep = this.STEP_CONFIRMATION;
        if (consumedDraft) {
          this.draftConsumed.emit();
        }
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.isSubmitting = false;
        const code = (err.error as any)?.code;
        if (err.status === 409 && (err.error as any)?.quote) {
          this.priceChangedMessage = this.translate.instant('SIGN_ORDER.CHECKOUT.PRICE_CHANGED');
          this.quote = (err.error as any).quote;
          this.quoteIsStale = false;
          this.quoteRefreshFailed = false;
        } else if (code === 'IDEMPOTENCY_KEY_REUSED') {
          this.errorMessage = this.translate.instant('SIGN_ORDER.CHECKOUT.IDEMPOTENCY_REUSED');
          this.isSubmitting = false;
        } else if (code === 'POSTER_PREVIEW_CHANGED') {
          this.priceChangedMessage = this.translate.instant('SIGN_ORDER.CHECKOUT.POSTER_CHANGED');
          this.confirmSnapshot = false;
          this.posterPreviewRevision = null;
          this.loadPosterPreview();
        } else {
          const msg = (err.error as any)?.message || this.translate.instant('SIGN_ORDER.CHECKOUT.ERROR_SUBMIT');
          this.errorMessage = msg;
          this.message.error(msg);
        }
        this.cdr.markForCheck();
      },
    });
  }

  private generateIdempotencyKey(): string {
    return crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substring(2)}`;
  }

  private resetIdempotencyKey(): void {
    this.idempotencyKey = '';
  }

  formatCents(cents: number, currency: string): string {
    const lang = this.translate.currentLang === 'de' ? 'de-DE' : 'en-US';
    const amount = (cents / 100).toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${amount} ${currency}`;
  }

  formatVatRate(basisPoints: number): string {
    return `${(basisPoints / 100).toFixed(1)}%`;
  }

  togglePriceTiers(): void {
    this.showPriceTiers = !this.showPriceTiers;
  }

  formatTierQuantityRange(tier: Pick<PricingTier, 'minQuantity' | 'maxQuantity'>): string {
    return tier.maxQuantity === null
      ? `${tier.minQuantity}+`
      : `${tier.minQuantity}–${tier.maxQuantity}`;
  }

  private setPresentationPricingTiers(tiers: readonly PricingTier[] | null | undefined): void {
    const sourceTiers = Array.isArray(tiers) ? tiers : [];
    this.presentationPricingTiers = sourceTiers.filter((tier, index) => this.isPresentationPricingTier(tier, index, sourceTiers.length));

    if (!this.presentationPricingTiers.length) {
      this.groupedPresentationPricingTiers = [];
      this.priceSummary = null;
      return;
    }

    this.groupedPresentationPricingTiers = this.groupPresentationPricingTiers(this.presentationPricingTiers);

    const prices = this.presentationPricingTiers.map((tier) => tier.unitNetCents);
    const lowestUnitNetCents = Math.min(...prices);
    const highestUnitNetCents = Math.max(...prices);
    this.priceSummary = {
      lowestUnitNetCents,
      highestUnitNetCents,
      isRange: lowestUnitNetCents !== highestUnitNetCents,
    };
  }

  private groupPresentationPricingTiers(tiers: readonly PricingTier[]): readonly DisplayPricingTier[] {
    const grouped: DisplayPricingTier[] = [];

    for (const tier of tiers) {
      const previous = grouped[grouped.length - 1];
      const nextQuantity = previous && previous.maxQuantity !== null
        ? previous.maxQuantity + 1
        : null;
      const isContiguous = nextQuantity !== null && Number.isSafeInteger(nextQuantity) && nextQuantity === tier.minQuantity;

      if (previous && isContiguous && previous.unitNetCents === tier.unitNetCents) {
        grouped[grouped.length - 1] = {
          minQuantity: previous.minQuantity,
          maxQuantity: tier.maxQuantity,
          unitNetCents: previous.unitNetCents,
        };
        continue;
      }

      grouped.push({
        minQuantity: tier.minQuantity,
        maxQuantity: tier.maxQuantity,
        unitNetCents: tier.unitNetCents,
      });
    }

    return grouped;
  }

  private isPresentationPricingTier(tier: PricingTier, index: number, sourceLength: number): boolean {
    if (!tier || !Number.isSafeInteger(tier.minQuantity) || tier.minQuantity < 1) return false;
    if (!Number.isSafeInteger(tier.unitNetCents) || tier.unitNetCents < 0) return false;
    if (tier.maxQuantity === null) return index === sourceLength - 1;
    return Number.isSafeInteger(tier.maxQuantity) && tier.maxQuantity >= tier.minQuantity;
  }

  get currentPresentationTier(): PricingTier | null {
    const quantity = this.cachedCopyCount;
    return quantity > 0
      ? this.presentationPricingTiers.find((tier) => quantity >= tier.minQuantity && (tier.maxQuantity === null || quantity <= tier.maxQuantity)) || null
      : null;
  }

  get currentBaseTierSummaryTranslationKey(): string {
    return this.cachedCopyCount === 1
      ? 'SIGN_ORDER.CHECKOUT.CURRENT_BASE_TIER_SUMMARY_ONE'
      : 'SIGN_ORDER.CHECKOUT.CURRENT_BASE_TIER_SUMMARY_MANY';
  }

  get currentValidQuote(): QuoteResponse | null {
    const quote = this.quote;
    const totalQuantity = this.cachedCopyCount;
    if (!quote
      || this.isQuoteLoading
      || this.quoteIsStale
      || this.quoteRefreshFailed
      || totalQuantity <= 0
      || quote.totalQuantity !== totalQuantity
      || quote.drillingOption !== this.drillingOption) {
      return null;
    }

    return quote;
  }

  get currentTierPriceQuote(): QuoteResponse | null {
    const quote = this.quote;
    const product = this.product;
    if (!quote
      || !product
      || this.quoteRefreshFailed
      || this.cachedCopyCount <= 0
      || quote.totalQuantity !== this.cachedCopyCount
      || quote.product?.code !== product.code
      || quote.productPricingRevision !== product.productPricingRevision
      || (this.catalog?.settingsPricingRevision !== undefined
        && quote.settingsPricingRevision !== this.catalog.settingsPricingRevision)) {
      return null;
    }

    return quote;
  }

  get baseUnitPriceDisplay(): string | null {
    const quote = this.currentTierPriceQuote;
    const baseUnitNetCents = quote?.selectedSignTier?.unitNetCents;
    if (!quote
      || typeof baseUnitNetCents !== 'number'
      || !Number.isSafeInteger(baseUnitNetCents)
      || baseUnitNetCents < 0) {
      return null;
    }

    return this.formatCents(baseUnitNetCents, quote.currency || this.product?.currency || '');
  }

  get drillingUnitPriceDisplay(): string | null {
    const quote = this.currentTierPriceQuote;
    const drillingUnitNetCents = quote?.selectedSignTier?.drillingUnitNetCents;
    if (!quote
      || typeof drillingUnitNetCents !== 'number'
      || !Number.isSafeInteger(drillingUnitNetCents)
      || drillingUnitNetCents < 0) {
      return null;
    }

    return this.formatCents(drillingUnitNetCents, quote.currency || this.product?.currency || '');
  }

  getCurrency(): string {
    return this.quote?.currency || this.product?.currency || 'EUR';
  }

  trackById(_index: number, item: PropertyItem): string {
    return item.id;
  }

  loadLegalManifest(): void {
    const isEn = (this.translate.currentLang || 'de') === 'en';
    this.legalLinks = [
      { label: 'SIGN_ORDER.LEGAL.LINK_TERMS', url: `${DOMAIN}/schilderbestellbedingungen`, enUrl: `${DOMAIN}/sign-order-terms`, deUrl: `${DOMAIN}/schilderbestellbedingungen` },
      { label: 'SIGN_ORDER.LEGAL.LINK_GENERAL_TERMS', url: `${DOMAIN}/agb`, enUrl: `${DOMAIN}/terms`, deUrl: `${DOMAIN}/agb` },
      { label: 'SIGN_ORDER.LEGAL.LINK_PRIVACY', url: `${DOMAIN}/datenschutz`, enUrl: `${DOMAIN}/privacy`, deUrl: `${DOMAIN}/datenschutz` },
      { label: 'SIGN_ORDER.LEGAL.LINK_LEGAL_NOTICE', url: `${DOMAIN}/impressum`, enUrl: `${DOMAIN}/legal-notice`, deUrl: `${DOMAIN}/impressum` },
    ].map(link => ({ label: link.label, url: isEn ? link.enUrl : link.deUrl }));
    this.legalUnavailable = false;

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const en = (this.translate.currentLang || 'de') === 'en';
      this.legalLinks = [
        { label: 'SIGN_ORDER.LEGAL.LINK_TERMS', url: en ? `${DOMAIN}/sign-order-terms` : `${DOMAIN}/schilderbestellbedingungen` },
        { label: 'SIGN_ORDER.LEGAL.LINK_GENERAL_TERMS', url: en ? `${DOMAIN}/terms` : `${DOMAIN}/agb` },
        { label: 'SIGN_ORDER.LEGAL.LINK_PRIVACY', url: en ? `${DOMAIN}/privacy` : `${DOMAIN}/datenschutz` },
        { label: 'SIGN_ORDER.LEGAL.LINK_LEGAL_NOTICE', url: en ? `${DOMAIN}/legal-notice` : `${DOMAIN}/impressum` },
      ];
    });

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.posterPreviewResponse) {
        this.invalidatePosterPreview();
        this.loadPosterPreview();
      }
    });
  }

  loadPosterPreview(): void {
    if (!this.quote || this.cachedItemCount === 0) return;
    this.clearPosterPreview();
    this.posterPreviewLoading = true;
    this.confirmSnapshot = false;
    this.posterLoadGen++;
    const gen = this.posterLoadGen;
    this.cdr.markForCheck();

    const payload = this.buildSelectionPayload();

    this.signOrderService.postPosterPreview(payload).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (this.posterLoadGen !== gen) return;
        this.posterPreviewResponse = res;
        this.posterPreviewRevision = res.previewRevision;
        this.posterPreviewUnavailable = false;
        this.posterPreviewAssetsReady = true;
        if (res.properties.length > 0) {
          this.posterPreviewSelectedPropertyId = res.properties[0].propertyId;
          this.renderPosterPreview();
          return;
        }
        this.posterPreviewLoading = false;
        this.posterPreviewAssetsReady = false;
        this.posterPreviewUnavailable = true;
        this.confirmSnapshot = false;
        this.posterPreviewRevision = null;
        this.cdr.markForCheck();
      },
      error: () => {
        if (this.posterLoadGen !== gen) return;
        this.posterPreviewLoading = false;
        this.posterPreviewAssetsReady = false;
        this.posterPreviewUnavailable = true;
        this.confirmSnapshot = false;
        this.posterPreviewRevision = null;
        this.cdr.markForCheck();
      },
    });
  }

  private buildSelectionPayload(): PosterPreviewRequest {
    const payload: PosterPreviewRequest = {
      drillingOption: this.drillingOption,
      locale: this.translate.currentLang || 'de',
    };
    if (this.selection.selectAllData) {
      payload.selectionId = this.selection.selectAllData.selectionId;
      if (this.selection.selectAllData.excludedIds.size > 0) {
        payload.excludedPropertyIds = Array.from(this.selection.selectAllData.excludedIds);
      }
      if (Object.keys(this.selection.selectAllData.copyOverrides).length > 0) {
        payload.copyOverrides = { ...this.selection.selectAllData.copyOverrides };
      }
    } else {
      payload.items = Array.from(this.selection.selectedPropertyIds).map(id => ({
        propertyId: id, copies: this.selection.copiesMap[id] || 1,
      }));
    }
    return payload;
  }

  private posterRenderGen = 0;
  private posterLoadGen = 0;

  onPosterPropertyChange(): void {
    if (!this.posterPreviewAssetsReady || this.posterPreviewUnavailable) return;
    this.renderPosterPreview();
  }

  private async renderPosterPreview(): Promise<void> {
    if (!this.posterPreviewResponse || !this.posterPreviewSelectedPropertyId) return;
    if (!this.posterPreviewAssetsReady || this.posterPreviewUnavailable) return;
    const prop = this.posterPreviewResponse.properties.find(p => p.propertyId === this.posterPreviewSelectedPropertyId);
    if (!prop) {
      this.posterPreviewLoading = false;
      this.posterPreviewUnavailable = true;
      this.posterPreviewReady = false;
      this.confirmSnapshot = false;
      this.disposePosterPreviewRender();
      this.clearPosterPreviewCanvases();
      this.cdr.markForCheck();
      return;
    }

    this.posterPreviewLoading = true;
    this.posterRenderGen++;
    const gen = this.posterRenderGen;
    let previewRender: QrPosterPdfPreviewRender | null = null;
    this.disposePosterPreviewRender();
    this.cdr.markForCheck();

    try {
      const blob = await this.qrPosterDocumentService.createPdfBlob(
        { descriptor: prop.descriptor },
        { renderSource: 'sign-order-canonical-customer-preview' },
      );
      if (this.posterRenderGen !== gen || this.posterPreviewSelectedPropertyId !== prop.propertyId) {
        return;
      }

      this.cdr.detectChanges();
      const renderCanvas = this.posterPreviewRenderCanvas?.nativeElement;
      if (!renderCanvas) {
        throw new Error('Unable to initialize the sign-order QR poster preview');
      }

      previewRender = this.qrPosterPdfPreviewService.createFirstPageRender(blob, renderCanvas);
      this.posterPreviewRender = previewRender;
      await previewRender.completed;

      if (this.posterRenderGen !== gen || this.posterPreviewSelectedPropertyId !== prop.propertyId) {
        previewRender.dispose();
        return;
      }

      this.copyPosterPreviewCanvas(renderCanvas, prop.descriptor.template.page);
      this.posterPreviewReady = true;
      this.posterPreviewUnavailable = false;
    } catch {
      if (this.posterRenderGen !== gen) return;
      if (this.posterPreviewRender === previewRender) {
        previewRender?.dispose();
        this.posterPreviewRender = null;
      }
      this.posterPreviewReady = false;
      this.posterPreviewUnavailable = true;
      this.confirmSnapshot = false;
      this.clearPosterPreviewCanvases();
    } finally {
      if (this.posterPreviewRender === previewRender) {
        previewRender?.dispose();
        this.posterPreviewRender = null;
      }
      if (this.posterRenderGen === gen) {
        this.posterPreviewLoading = false;
        this.cdr.markForCheck();
      }
    }
  }

  invalidatePosterPreview(): void {
    this.confirmSnapshot = false;
    this.posterPreviewRevision = null;
    this.posterPreviewAssetsReady = false;
    this.clearPosterPreview();
  }

  private clearPosterPreview(): void {
    this.posterLoadGen++;
    this.posterRenderGen++;
    this.disposePosterPreviewRender();
    this.clearPosterPreviewCanvases();
    this.posterPreviewReady = false;
    this.posterPreviewResponse = null;
    this.posterPreviewRevision = null;
    this.posterPreviewSelectedPropertyId = '';
    this.posterPreviewLoading = false;
    this.posterPreviewUnavailable = false;
    this.posterPreviewAssetsReady = false;
  }

  getSelectedPreviewProperty(): PosterPreviewProperty | null {
    if (!this.posterPreviewResponse) return null;
    return this.posterPreviewResponse.properties.find(p => p.propertyId === this.posterPreviewSelectedPropertyId) || this.posterPreviewResponse.properties[0] || null;
  }

  private copyPosterPreviewCanvas(source: HTMLCanvasElement, page: PosterPageDimensions): void {
    const target = this.posterPreviewCanvas?.nativeElement;
    if (!target || source.width <= 0 || source.height <= 0) {
      throw new Error('Unable to display the sign-order QR poster preview');
    }

    const context = target.getContext('2d', { alpha: false });
    if (!context) {
      throw new Error('Unable to display the sign-order QR poster preview');
    }

    target.width = source.width;
    target.height = source.height;
    context.drawImage(source, 0, 0);
    this.drawDrillingOverlay(context, target, page);
  }

  private drawDrillingOverlay(
    context: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    page: PosterPageDimensions,
  ): void {
    if (this.drillingOption !== 'with_drilling') return;

    const widthMm = page?.widthMm;
    const heightMm = page?.heightMm;
    if (
      !Number.isFinite(widthMm)
      || !Number.isFinite(heightMm)
      || widthMm <= DRILL_HOLE_EDGE_OFFSET_MM * 2
      || heightMm <= DRILL_HOLE_EDGE_OFFSET_MM * 2
      || canvas.width <= 0
      || canvas.height <= 0
    ) {
      return;
    }

    const scaleX = canvas.width / widthMm;
    const scaleY = canvas.height / heightMm;
    const scale = Math.min(scaleX, scaleY);
    if (!Number.isFinite(scaleX) || !Number.isFinite(scaleY) || !Number.isFinite(scale) || scale <= 0) return;

    const radius = DRILL_HOLE_RADIUS_MM * scale;
    const centers: ReadonlyArray<readonly [number, number]> = [
      [DRILL_HOLE_EDGE_OFFSET_MM, DRILL_HOLE_EDGE_OFFSET_MM],
      [widthMm - DRILL_HOLE_EDGE_OFFSET_MM, DRILL_HOLE_EDGE_OFFSET_MM],
      [DRILL_HOLE_EDGE_OFFSET_MM, heightMm - DRILL_HOLE_EDGE_OFFSET_MM],
      [widthMm - DRILL_HOLE_EDGE_OFFSET_MM, heightMm - DRILL_HOLE_EDGE_OFFSET_MM],
    ];

    context.save();
    context.fillStyle = 'rgba(255, 255, 255, 0.88)';
    context.strokeStyle = 'rgba(55, 65, 81, 0.72)';
    context.lineWidth = Math.max(1, DRILL_HOLE_OUTLINE_WIDTH_MM * scale);
    for (const [xMm, yMm] of centers) {
      context.beginPath();
      context.arc(xMm * scaleX, yMm * scaleY, radius, 0, Math.PI * 2);
      context.fill();
      context.stroke();
    }
    context.restore();
  }

  private disposePosterPreviewRender(): void {
    this.posterPreviewRender?.dispose();
    this.posterPreviewRender = null;
  }

  private clearPosterPreviewCanvases(): void {
    const visibleCanvas = this.posterPreviewCanvas?.nativeElement;
    if (visibleCanvas) {
      visibleCanvas.width = 0;
      visibleCanvas.height = 0;
    }

    const renderCanvas = this.posterPreviewRenderCanvas?.nativeElement;
    if (renderCanvas) {
      renderCanvas.width = 0;
      renderCanvas.height = 0;
    }
  }

  legalUnavailable = false;
  posterPreviewResponse: PosterPreviewResponse | null = null;
  posterPreviewLoading = false;
  posterPreviewUnavailable = false;
  posterPreviewAssetsReady = false;
  posterPreviewSelectedPropertyId = '';
  posterPreviewReady = false;
  private posterPreviewRender: QrPosterPdfPreviewRender | null = null;
  posterPreviewRevision: string | null = null;

  localizedText(text: { de: string; en: string; [key: string]: string } | undefined): string {
    if (!text) return '';
    const lang = this.translate.currentLang || 'de';
    return text[lang] || text.de || text.en || '';
  }

  localizedPaymentInstructions(instructions: { de: string; en: string; [key: string]: string } | undefined): string {
    if (!instructions) return '';
    const lang = this.translate.currentLang || 'de';
    return instructions[lang] || instructions.de || instructions.en || '';
  }

  loadImage(url: string): void {
    if (this.imageBlobUrls[url]) return;
    if (this.imageLoading[url]) return;
    this.imageLoading[url] = true;
    this.signOrderService.loadImageBlob(url).pipe(takeUntil(this.destroy$)).subscribe({
      next: (objectUrl) => {
        this.imageBlobUrls[url] = objectUrl;
        this.imageLoading[url] = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.imageLoading[url] = false;
        this.cdr.markForCheck();
      },
    });
  }

  getImageBlobUrl(url: string): string {
    return this.imageBlobUrls[url] || '';
  }

  revokeAllImageBlobUrls(): void {
    for (const url of Object.values(this.imageBlobUrls)) {
      URL.revokeObjectURL(url);
    }
    this.imageBlobUrls = {};
  }

  ngOnDestroy(): void {
    this.posterLoadGen++;
    this.posterRenderGen++;
    this.disposePosterPreviewRender();
    this.clearPosterPreviewCanvases();
    this.posterPreviewReady = false;
    if (this.copyOverrideSearchDebounce) { clearTimeout(this.copyOverrideSearchDebounce); }
    this.revokeAllImageBlobUrls();
    this.destroy$.next();
    this.destroy$.complete();
    this.quoteRefreshSub.unsubscribe();
  }
}
